import React, { useEffect, useState } from "react";
import axios from "axios";
import { ACCESS_TOKEN } from "utils/storage-keys";
import { getItem, getSiteCode } from "utils/local-storage";
import env from "utils/env";

import { Col, Row } from "antd";
import PremiumFeature, { Benefit } from "./blocks/PackageBenefit";
import UpgradePopup from "components/Popup/UpgradePopup";
import Loading from "components/Loading/Loading";

import IconStart from "../../../assets/img/icon-stars.svg";
import QR from "../../../assets/img/open-app-qr.jpg";
import "./UpgradePackage.css";
import { useTranslation } from "react-i18next";
import GlobalStore from "stores/globalStore";
import { formatDate } from "utils/helpers";
import { useGetUserProfileApi } from "../user/useUserApi";

type Props = {
  upgradeType?: "elsa_speak" | "premium";
};

type CatalogVariant = {
  base_price: number;
  price: number;
  discount_percent: number;
  interface: {
    package: string;
    package_name: string;
  };
};

type CatalogResponse = {
  code: "elsa_speak" | "premium" | "ai_role_play_sa";
  name: string;
  photo_url: string;
  benefits: Benefit[];
  variants: CatalogVariant[];
};

type PackageInfo = {
  membership: string;
  package_name: string;
  base_price: number;
  discount_price: number;
  discount_percent: number;
};

type UserInfo = {
  total: number;
  metadata: {
    code: string;
    expire_at: number;
  };
};

type UpgradeInfo = {
  name: string;
  photo: string;
  packages: PackageInfo[];
  benefits: Benefit[];
};

const UpgradePackage: React.FC<Props> = ({ upgradeType = "premium" }) => {
  const { isLoading: profileLoading } = useGetUserProfileApi();
  const config = env();
  const token = getItem(ACCESS_TOKEN);
  const siteCode = getSiteCode();
  const { entitlements } = GlobalStore.useContainer();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    total: 0,
    metadata: {
      code: "free",
      expire_at: 0,
    },
  });
  const [showPopup, setShowPopup] = useState(false);
  const [membership, setMembership] = useState("");
  const [upgradeInfo, setUpgradeInfo] = useState<UpgradeInfo>({
    photo: "",
    name: "",
    benefits: [],
    packages: [],
  });
  const isProLifetime = entitlements?.tier.code === "pro" && !entitlements?.tier.expire_at;

  const { t } = useTranslation();

  useEffect(() => {
    if (!token || profileLoading) return;
    setLoading(true);

    const requestConfig = {
      headers: {
        "X-Session-Token": token,
      },
    };

    const fetchData = async () => {
      try {
        const unusedMoney = await axios.get(`${config.backendPaymentApiUrl}/account/get-unused-money?currency=twd&site_code=${siteCode}`, requestConfig);
        setUserInfo({
          total: unusedMoney.data?.total,
          metadata: unusedMoney.data?.metadata,
        });
      } catch (error) {
        console.log(error);
      }

      try {
        const catalog = await axios.get(`${config.baseApiUrl}/product-catalog/v1/site/${siteCode}/upgrade?country=TW&language=zh-tw`, requestConfig);
        const catalogRes: CatalogResponse[] = catalog.data?.catalogs || [];
        if (!catalogRes.length) {
          setLoading(false);
          return setUpgradeInfo({
            photo: "",
            name: "",
            benefits: [],
            packages: [],
          });
        }
        const premiumPackages = catalogRes.find((item) => item.code === "premium");
        const proPackages = catalogRes.find((item) => item.code === "elsa_speak");
        const packages = upgradeType === "elsa_speak" ? proPackages : premiumPackages;
        if (!packages) {
          setLoading(false);
          return setUpgradeInfo({
            photo: "",
            name: "",
            benefits: [],
            packages: [],
          });
        }

        const packagesInfo: PackageInfo[] = packages?.variants.map((item) => ({
          membership: item?.interface?.package,
          package_name: item?.interface?.package_name,
          base_price: item?.base_price,
          discount_price: item?.price,
          discount_percent: item?.discount_percent,
        }));

        setUpgradeInfo({
          name: packages?.name || "",
          photo: packages?.photo_url || "",
          benefits: packages?.benefits || [],
          packages: packagesInfo,
        });
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    };

    fetchData();
  }, [token, upgradeType, siteCode, config, profileLoading]);

  const handleSubmit = () => {
    setShowPopup(true);
  };

  return (
    <div className="upgrade-premium">
      {loading ? (
        <Row justify="center">
          <Loading color="#ccc" />
        </Row>
      ) : (
        <Row>
          <Col md={12} span={24}>
            <div className="upgrade-premium__current">
              <h3 className="title">{t("courses.current_package")}</h3>
              <div className="package">
                <p className={`package-name ${entitlements?.tier.code}`}>ELSA {entitlements?.tier && entitlements?.tier.code[0].toUpperCase() + entitlements?.tier.code.substring(1)}</p>
                <div className="next-bill">
                  {isProLifetime ? (
                    <strong>Lifetime</strong>
                  ) : entitlements?.tier.expire_at ? (
                    <>
                      <p>{t("courses.expired_date")}:</p>
                      <strong>{formatDate(new Date(entitlements?.tier.expire_at * 1000))}</strong>
                    </>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
            <div className="upgrade-premium__upgrade">
              <h3 className="title">{upgradeType === "premium" ? t("courses.upgrade_to_pre") : t("courses.upgrade_to_pro")}</h3>
              {entitlements?.tier.code === "premium" ? (
                <div className="note">
                  <p>{t("courses.scan_qr_code")}</p>
                  <img src={QR} alt="" width={240} />
                </div>
              ) : !upgradeInfo.packages.length ? (
                <p className="note">{t("courses.upgrade_not_available")}</p>
              ) : (
                upgradeInfo.packages.length && (
                  <>
                    <div className="package-list">
                      {upgradeInfo.packages.map((item) => (
                        <label className="package" htmlFor={item.membership} key={item.membership}>
                          {item.membership.includes("lifetime_membership") && (
                            <p className="head">
                              <img src={IconStart} alt="" /> {t("courses.conversation_bundle_info")}
                            </p>
                          )}
                          <div className="content">
                            <input type="radio" name="membership_name" value={item.membership} id={item.membership} onChange={(e) => setMembership(e.target.value)} checked={membership === item.membership} />
                            <p className="package-name">{item.package_name}</p>
                            {isProLifetime && item.membership.includes("lifetime_membership_premium") ? (
                              <p className="price">{(item.discount_price - userInfo.total).toLocaleString("tw-TW")} 元</p>
                            ) : (
                              <>
                                {!!item.discount_percent && <div className="discount">-{item.discount_percent}%</div>}
                                <p className="price">{item.discount_price.toLocaleString("tw-TW")} 元</p>
                              </>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    {!!userInfo.total && !isProLifetime && <div className="upgrade-premium__balance">升級至Premium套餐時，將扣除您剩餘的 {userInfo.total.toLocaleString("tw-TW")} 元餘額。</div>}
                    <button className="btn-primary" onClick={handleSubmit} disabled={!membership}>
                      {t("continue")}
                    </button>
                  </>
                )
              )}
            </div>
          </Col>
          {entitlements?.tier.code !== "premium" && (
            <Col md={12} span={24}>
              <PremiumFeature photo={upgradeInfo.photo} benefit={upgradeInfo.benefits || []} title={`${upgradeInfo.name}的福利:`} />
            </Col>
          )}
        </Row>
      )}
      {showPopup && <UpgradePopup membership={membership} money={userInfo.total || 0} siteCode={siteCode} setShowPopup={setShowPopup} />}
    </div>
  );
};

export default UpgradePackage;
